import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSettings, FiPlay, FiHeart, FiHeart as FiHeartFilled } from 'react-icons/fi';

// 格式化时间为 MM:SS 格式
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const LearningHistory = () => {
  const [activeTab, setActiveTab] = useState('recent');
  const [learnedVideos, setLearnedVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [learningProgress, setLearningProgress] = useState({});
  const [collectedVideos, setCollectedVideos] = useState([]);
  const navigate = useNavigate();

  const getLearnedVideos = async () => {
    try {
      // 从服务器API获取所有视频数据
      const response = await fetch('/api/videos');
      const data = await response.json();
      
      let uploadedVideos = [];
      if (data.success && data.videos) {
        // 转换数据格式以匹配前端期望的格式
        uploadedVideos = data.videos.map(video => ({
          id: video.videoId,
          title: video.title,
          description: '',
          category: video.category,
          subcategory: video.subcategory || '',
          level: 'Starters',
          duration: video.duration || 0,
          fileUrl: video.videoUrl.startsWith('http') ? video.videoUrl : `${window.location.origin}${video.videoUrl}`,
          subtitleUrl: video.subtitleFiles && video.subtitleFiles.length > 0 
            ? (video.subtitleFiles[0].startsWith('http') 
                ? video.subtitleFiles[0] 
                : `${window.location.origin}/uploads/${video.subtitleFiles[0]}`)
            : '',
          posterUrl: video.posterUrl 
            ? (video.posterUrl.startsWith('http') 
                ? video.posterUrl 
                : `${window.location.origin}${video.posterUrl}`)
            : '',
          fileType: video.fileType || 'video',
          hidden: false,
          uploadedAt: video.uploadTime,
          subtitleFiles: video.subtitleFiles || [],
          uploadDate: video.uploadTime
        }));
      } else {
        // 出错时尝试从localStorage获取作为备用
        uploadedVideos = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
      }
      
      const collectedVideos = JSON.parse(localStorage.getItem('collectedVideos') || '[]');
      const learnedCourses = JSON.parse(localStorage.getItem('learnedCourses') || '[]');
      
      const videos = uploadedVideos.map(video => ({
        ...video,
        collected: collectedVideos.includes(video.id),
        learned: learnedCourses.includes(video.id) || learnedCourses.includes(`uploaded_${video.id}`)
      }));
      
      setLearnedVideos(videos);
      
      const progress = {};
      videos.forEach(video => {
        // 模拟进度：已学习的视频设为100%，未学习的设为0%
        progress[video.id] = video.learned ? 100 : 0;
      });
      setLearningProgress(progress);
    } catch (error) {
      console.error('Failed to get learned videos:', error);
      // 出错时尝试从localStorage获取作为备用
      try {
        const uploadedVideos = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
        const collectedVideos = JSON.parse(localStorage.getItem('collectedVideos') || '[]');
        const learnedCourses = JSON.parse(localStorage.getItem('learnedCourses') || '[]');
        
        const videos = uploadedVideos.map(video => ({
          ...video,
          collected: collectedVideos.includes(video.id),
          learned: learnedCourses.includes(video.id) || learnedCourses.includes(`uploaded_${video.id}`)
        }));
        
        setLearnedVideos(videos);
        
        const progress = {};
        videos.forEach(video => {
          progress[video.id] = video.learned ? 100 : 0;
        });
        setLearningProgress(progress);
      } catch (localError) {
        setLearnedVideos([]);
      }
    }
  };

  const filterVideos = () => {
    let filtered = [...learnedVideos];
    
    switch (activeTab) {
      case 'recent':
        // 最近学习：显示所有视频，按上传时间排序
        filtered.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
        break;
      case 'completed':
        // 已完成：显示已学习的视频
        filtered = filtered.filter(video => video.learned);
        break;
      case 'collected':
        // 已收藏：显示已收藏的视频
        filtered = filtered.filter(video => video.collected);
        break;
      default:
        break;
    }
    
    setFilteredVideos(filtered);
  };

  // 加载收藏的视频
  useEffect(() => {
    try {
      const collected = JSON.parse(localStorage.getItem('collectedVideos') || '[]');
      setCollectedVideos(collected);
    } catch (error) {
      console.error('Failed to load collected videos:', error);
      setCollectedVideos([]);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await getLearnedVideos();
    };
    fetchData();
  }, [collectedVideos]);

  useEffect(() => {
    filterVideos();
  }, [learnedVideos, activeTab, learningProgress]);

  // 切换收藏状态
  const toggleCollect = (e, videoId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const isCollected = collectedVideos.includes(videoId);
    let newCollected;
    
    if (isCollected) {
      newCollected = collectedVideos.filter(id => id !== videoId);
    } else {
      newCollected = [...collectedVideos, videoId];
    }
    
    setCollectedVideos(newCollected);
    localStorage.setItem('collectedVideos', JSON.stringify(newCollected));
  };

  return (
    <div style={{ height: '100vh', backgroundColor: '#f8f9fa', color: '#1a2b4c', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* 返回按钮 */}
      <div style={{ padding: '0.75rem 1rem 0.5rem 1rem' }}>
        <button 
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#0066ff',
            textDecoration: 'none',
            padding: '0.5rem 0.7rem',
            borderRadius: '8px',
            backgroundColor: 'white',
            border: '1px solid rgba(0, 102, 255, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            flexShrink: 0,
            fontSize: '1.15rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.05)';
            e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.boxShadow = 'none';
          }}
        >
          <FiArrowLeft />
        </button>
      </div>

      <div style={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '1rem'
      }}>
        <div className="container" style={{ 
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingTop: '1rem'
          }}>
            <div style={{
              display: 'flex',
              gap: '0.25rem',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '0.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
            }}>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: activeTab === 'recent' ? '#0066ff' : 'rgba(0, 102, 255, 0.1)',
                  color: activeTab === 'recent' ? 'white' : '#1a2b4c',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setActiveTab('recent')}
                onMouseEnter={(e) => {
                  if (activeTab !== 'recent') {
                    e.target.style.backgroundColor = '#0066ff';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'recent') {
                    e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.1)';
                    e.target.style.color = '#1a2b4c';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                最近学习
              </button>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: activeTab === 'completed' ? '#0066ff' : 'rgba(0, 102, 255, 0.1)',
                  color: activeTab === 'completed' ? 'white' : '#1a2b4c',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setActiveTab('completed')}
                onMouseEnter={(e) => {
                  if (activeTab !== 'completed') {
                    e.target.style.backgroundColor = '#0066ff';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'completed') {
                    e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.1)';
                    e.target.style.color = '#1a2b4c';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                已完成
              </button>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: activeTab === 'collected' ? '#0066ff' : 'rgba(0, 102, 255, 0.1)',
                  color: activeTab === 'collected' ? 'white' : '#1a2b4c',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setActiveTab('collected')}
                onMouseEnter={(e) => {
                  if (activeTab !== 'collected') {
                    e.target.style.backgroundColor = '#0066ff';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'collected') {
                    e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.1)';
                    e.target.style.color = '#1a2b4c';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                已收藏
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '2rem 0' }}>
        <div className="container" style={{ 
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          {filteredVideos.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 0',
              color: '#666'
            }}>
              <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>还没有学习记录</p>
              <p style={{ fontSize: '1rem' }}>去课程中学习吧！</p>
              <button
                onClick={() => navigate('/courses')}
                style={{
                  marginTop: '2rem',
                  padding: '0.75rem 2rem',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                浏览课程
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1.5rem',
              justifyContent: 'flex-start'
            }}>
              {filteredVideos.map((video) => (
                <Link 
                  key={video.id}
                  to={`/lesson/${video.id}`}
                  style={{
                    textDecoration: 'none',
                    color: '#1a2b4c'
                  }}
                >
                  <div 
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0, 102, 255, 0.12)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '2px solid rgba(0, 102, 255, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 16px 32px rgba(0, 102, 255, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(0, 102, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 102, 255, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(0, 102, 255, 0.2)';
                    }}
                  >
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      paddingBottom: '56.25%', // 16:9比例
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0'
                    }}>
                      {video.posterUrl ? (
                        <img 
                          src={video.posterUrl} 
                          alt={video.title}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e0e0e0',
                          color: '#666'
                        }}>
                          🎬 视频封面
                        </div>
                      )}
                      
                      {/* 收藏按钮 */}
                      <button
                        onClick={(e) => toggleCollect(e, video.id)}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          zIndex: 10
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FiHeart 
                          size={20} 
                          color={collectedVideos.includes(video.id) ? '#ff4757' : '#ff4757'}
                          fill={collectedVideos.includes(video.id) ? '#ff4757' : 'none'}
                        />
                      </button>
                    </div>
                    
                    <div style={{ padding: '0.75rem', flex: '1', display: 'flex', flexDirection: 'column' }}>
                      {/* 视频标题 */}
                      <h3 style={{
                        color: '#333333',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        margin: '0 0 0.3rem 0',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '36px'
                      }}>
                        {video.title}
                      </h3>
                      
                      {/* 分类、难易度、时长、文件类型 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <span style={{
                            backgroundColor: 'rgba(0, 102, 255, 0.1)',
                            color: '#0066ff',
                            padding: '2px 8px',
                            borderRadius: '16px',
                            fontSize: '10px',
                            fontWeight: '600',
                            border: '1px solid rgba(0, 102, 255, 0.2)'
                          }}>
                            {(() => {
                              const categoryMap = {
                                'universe': '宇宙与空间科学',
                                'physics': '物理学',
                                'chemistry': '化学',
                                'earth': '地球科学',
                                'biology': '基础生物学',
                                'plants': '植物学',
                                'animals': '动物学',
                                'microbes': '微生物学',
                                'ecology': '生态学',
                                'human': '人体科学'
                              };
                              return categoryMap[video.category] || (video.category ? video.category : '未分类');
                            })()}
                          </span>
                          <span style={{
                            backgroundColor: 'rgba(134, 239, 172, 0.3)',
                            color: '#15803d',
                            padding: '2px 8px',
                            borderRadius: '16px',
                            fontSize: '10px',
                            fontWeight: '600',
                            border: '1px solid rgba(134, 239, 172, 0.4)'
                          }}>
                            {(() => {
                              const levelMap = {
                                'Starters': 'Starters',
                                'Movers': 'Movers',
                                'Flyers': 'Flyers',
                                'KET': 'KET',
                                'PET': 'PET',
                                'FCE': 'FCE'
                              };
                              return levelMap[video.level] || (video.level ? video.level : '未设置');
                            })()}
                          </span>
                          <span style={{
                            backgroundColor: 'rgba(251, 191, 36, 0.2)',
                            color: '#b45309',
                            padding: '2px 8px',
                            borderRadius: '16px',
                            fontSize: '10px',
                            fontWeight: '600',
                            border: '1px solid rgba(251, 191, 36, 0.3)'
                          }}>
                            {video.duration ? formatTime(video.duration) : '未知时长'}
                          </span>
                          <span style={{
                            backgroundColor: 'rgba(192, 132, 252, 0.2)',
                            color: '#7c3aed',
                            padding: '2px 8px',
                            borderRadius: '16px',
                            fontSize: '10px',
                            fontWeight: '600',
                            border: '1px solid rgba(192, 132, 252, 0.3)'
                          }}>
                            {video.fileType === 'audio' ? '🎵 音频' : '🎬 视频'}
                          </span>
                        </div>
                      </div>
                      {/* 上传日期、学习时间、学习进度 */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '0.3rem'
                      }}>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#666'
                        }}>
                          {new Date(video.uploadedAt).toLocaleDateString()}
                        </span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#666'
                        }}>
                          学习于 {new Date(video.uploadedAt).toLocaleDateString()}
                        </span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#1976d2',
                          fontWeight: '500'
                        }}>
                          查看 {learningProgress[video.id]}%
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningHistory;